import {
    Form,
    FormField,
    FormSection,
    FormType,
    DropdownData,
    FilteredValue,
    InputType,
    ReactiveFormChangedField,
    ReactiveFormValue,
} from '@fitogest/reactive-form-builder/interfaces';
import { list } from 'src/app/common/classes/request-builder';
import { getBackNameCountry } from 'src/app/common/country';
import { clearDropdownSelected } from '@fitogest/reactive-form-builder/utils';
import { EntidadModel } from 'src/app/models/reto/entidad.model';

export interface EntidadesDropdownOption {
    label: string;
    value: string | null;
}

export class EntidadesFormDefinition {
    private readonly formType!: FormType;

    public readonly countries: DropdownData<string> = list<string>(['...', null]);

    public readonly provinces: DropdownData<string> = list<string>(['...', null]);

    public model: ReactiveFormValue<EntidadModel> = {} as ReactiveFormValue<EntidadModel>;

    public lastChangedFieldName: ReactiveFormChangedField<EntidadModel> = '';

    public formFields!: Form<EntidadModel>;

    public fieldNames: FormField<EntidadModel>[] = [];

    public fieldLabels: string[] = [];

    /**
     * Creates the entity form definition shared by the entity screen and its modals.
     *
     * The definition centralizes sections, fields, dropdown lists and header metadata
     * so creation, edition, informative and delete modes use the same source of truth.
     *
     * @constructor
     * @public
     *
     * @param type - Request type used to define the form behaviour.
     * @param model - Optional entity model used as initial form data.
     * @param table - Optional table configuration used by table-based form modes.
     * The first item is assigned as table data and the second item as table style.
     *
     * @returns A new entity form definition instance.
     *
     * @example
     * ```ts
     * const form = new EntidadesFormDefinition(FormType.CREATION, {});
     * ```
     *
     * @see FormType
     * @see Form
     * @see FormSection
     * @see EntidadModel
     */
    constructor(
        type: FormType,
        model: Partial<EntidadModel> | null = {},
        table: (string[] | string[][])[] | null = null,
    ) {
        this.formType = +type;
        this.model = this.normalizeModel(model);

        this.formFields = {
            name: 'entidades',
            header: this.getHeader(),
            type: this.formType,
            sections: this.getFormSections(),
            table: table === null
                ? null
                : { title: '', showIndex: false, data: table?.[0], style: table?.[1] },
            uploadButton: {
                isEnabled: false,
                label: '',
            },
            signed: false,
        };
    }// constructor();

    /**
     * Replaces country dropdown options while keeping the same dropdown object
     * reference used by the reactive form builder.
     *
     * @public
     *
     * @param countries - Country options ready to be displayed by the dropdown.
     *
     * @returns Nothing.
     */
    public setCountryOptions(countries: EntidadesDropdownOption[]): void {
        this.countries.values = [...countries];
        this.countries.filtered = [...countries];
    }// setCountryOptions();

    /**
     * Replaces province dropdown options while keeping the same dropdown object
     * reference used by the reactive form builder.
     *
     * @public
     *
     * @param provinces - Province options ready to be displayed by the dropdown.
     *
     * @returns Nothing.
     */
    public setProvinceOptions(provinces: EntidadesDropdownOption[]): void {
        this.provinces.values = [...provinces];
        this.provinces.filtered = [...provinces];
    }// setProvinceOptions();

    /**
     * Clears province options without replacing the dropdown object reference.
     *
     * @public
     *
     * @returns Nothing.
     */
    public resetProvinceOptions(): void {
        const placeholder: FilteredValue<string> = { label: '...', value: null };

        clearDropdownSelected(this.provinces);
        this.provinces.values = [placeholder];
        this.provinces.filtered = [placeholder];
    }// resetProvinceOptions();

    /**
     * Builds and returns the identification fields for the entity form.
     *
     * @public
     *
     * @returns The identification form fields sorted by display order.
     *
     * @remarks
     * Visible fields are also appended to `fieldNames` and their labels are
     * appended to `fieldLabels`, allowing the same definition to be reused by
     * export or table generation logic.
     *
     * @see FormField
     * @see EntidadModel
     * @see InputType
     */
    public getIdentificationFields(): FormField<EntidadModel>[] {
        const identificationFields: FormField<EntidadModel>[] = [
            {
                name: 'nombre',
                vinculatedFields: undefined,
                label: 'Nombre o Razón Social',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: true,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true,
                },
                order: 1,
            },
            {
                name: 'apellidos',
                vinculatedFields: undefined,
                label: 'Apellidos',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true,
                },
                order: 2,
            },
            {
                name: 'nif',
                vinculatedFields: undefined,
                label: 'NIF / CIF',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true,
                },
                order: 3,
            },
            {
                name: 'pais',
                vinculatedFields: undefined,
                label: 'País',
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    isEditable: true,
                    data: this.countries,
                    defaultValue: this.model.pais ?? 'ES',
                    optionLabel: 'label',
                    optionValue: 'value',
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true,
                },
                order: 4,
            },
            {
                name: 'provincia',
                vinculatedFields: undefined,
                label: 'Provincia',
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    isEditable: true,
                    data: this.provinces,
                    optionLabel: 'label',
                    optionValue: 'value',
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true,
                },
                order: 5,
            },
            {
                name: 'localidad',
                vinculatedFields: undefined,
                label: 'Localidad (Código INE)',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true,
                },
                order: 6,
            },
        ];

        this.appendVisibleFields(identificationFields);
        return identificationFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }// getIdentificationFields();

    /**
     * Builds and returns the address fields for the entity form.
     *
     * @public
     *
     * @returns The address form fields sorted by display order.
     *
     * @see FormField
     * @see EntidadModel
     * @see InputType
     */
    public getAddressFields(): FormField<EntidadModel>[] {
        const addressFields: FormField<EntidadModel>[] = [
            {
                name: 'cp',
                vinculatedFields: undefined,
                label: 'Código postal',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true,
                },
                order: 1,
            },
            {
                name: 'direccion',
                vinculatedFields: undefined,
                label: 'Dirección',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true,
                },
                order: 2,
            },
        ];

        this.appendVisibleFields(addressFields);
        return addressFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }// getAddressFields();

    /**
     * Builds and returns the additional data fields for the entity form.
     *
     * @public
     *
     * @returns The additional data form fields sorted by display order.
     *
     * @see FormField
     * @see EntidadModel
     * @see InputType
     */
    public getAdditionalFields(): FormField<EntidadModel>[] {
        const additionalFields: FormField<EntidadModel>[] = [
            {
                name: 'email',
                vinculatedFields: undefined,
                label: 'Email',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true,
                },
                order: 1,
            },
            {
                name: 'telefono',
                vinculatedFields: undefined,
                label: 'Teléfono',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true,
                },
                order: 2,
            },
            {
                name: 'fax',
                vinculatedFields: undefined,
                label: 'Fax',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true,
                },
                order: 3,
            },
            {
                name: 'roposs',
                vinculatedFields: undefined,
                label: 'ROPO SS',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true,
                },
                order: 4,
            },
            {
                name: 'ropost',
                vinculatedFields: undefined,
                label: 'ROPO ST',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true,
                },
                order: 5,
            },
            {
                name: 'roposu',
                vinculatedFields: undefined,
                label: 'ROPO SU',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true,
                },
                order: 6,
            },
            {
                name: 'caducidadss',
                vinculatedFields: undefined,
                label: 'Fecha caducidad SS',
                input: {
                    type: InputType.CALENDAR,
                    isEditable: true,
                    calendarConfiguration: { calendarView: 'date', calendarDateFormat: 'dd/mm/yy' },
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true,
                },
                order: 7,
            },
            {
                name: 'caducidadst',
                vinculatedFields: undefined,
                label: 'Fecha caducidad ST',
                input: {
                    type: InputType.CALENDAR,
                    isEditable: true,
                    calendarConfiguration: { calendarView: 'date', calendarDateFormat: 'dd/mm/yy' },
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true,
                },
                order: 8,
            },
            {
                name: 'caducidadsu',
                vinculatedFields: undefined,
                label: 'Fecha caducidad SU',
                input: {
                    type: InputType.CALENDAR,
                    isEditable: true,
                    calendarConfiguration: { calendarView: 'date', calendarDateFormat: 'dd/mm/yy' },
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true,
                },
                order: 9,
            },
        ];

        this.appendVisibleFields(additionalFields);
        return additionalFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }// getAdditionalFields();

    /**
     * Builds and returns the hidden entity form fields required by submissions.
     *
     * @public
     *
     * @returns The hidden form fields sorted by display order.
     *
     * @see FormField
     * @see EntidadModel
     * @see InputType.HIDDEN
     */
    public getHiddenFields(): FormField<EntidadModel>[] {
        const hiddenFields: FormField<EntidadModel>[] = [
            {
                name: 'id',
                vinculatedFields: undefined,
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    defaultValue: this.model.id,
                    isEditable: false,
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true,
                },
                order: 1,
            },
            {
                name: 'id_cliente',
                vinculatedFields: undefined,
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    defaultValue: this.model.id_cliente,
                    isEditable: false,
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true,
                },
                order: 2,
            },
            {
                name: 'id_usuario',
                vinculatedFields: undefined,
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    defaultValue: this.model.id_usuario,
                    isEditable: false,
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true,
                },
                order: 3,
            },
        ];

        this.appendVisibleFields(hiddenFields);
        return hiddenFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }// getHiddenFields();

    /**
     * Builds and returns all entity form sections.
     *
     * @private
     *
     * @returns The entity form sections sorted by display order.
     *
     * @see FormSection
     * @see EntidadModel
     */
    private getFormSections(): FormSection<EntidadModel>[] {
        const sections: FormSection<EntidadModel>[] = [
            {
                name: 'Datos generales',
                fields: this.getIdentificationFields(),
                width: 12,
                isVisible: true,
                order: 1,
            },
            {
                name: 'Dirección',
                fields: this.getAddressFields(),
                width: 12,
                isVisible: true,
                order: 2,
            },
            {
                name: 'Datos adicionales',
                fields: this.getAdditionalFields(),
                width: 12,
                isVisible: true,
                order: 3,
            },
            {
                name: '',
                fields: this.getHiddenFields(),
                width: 12,
                isVisible: true,
                order: 4,
            },
        ];

        return sections.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }// getFormSections();

    /**
     * Returns the visible header according to the current form type.
     *
     * @private
     *
     * @returns The header text displayed by the reactive form builder.
     */
    private getHeader(): string {
        if (this.formType === FormType.CREATION) {
            return 'Crear entidad';
        }// if();

        if (this.formType === FormType.DUPLICATION) {
            return 'Duplicar entidad';
        }// if();

        if (this.formType === FormType.DELETION) {
            return 'Eliminar entidad';
        }// if();

        if (this.formType === FormType.INFORMATIVE) {
            return 'Datos de la entidad';
        }// if();

        return 'Actualizar entidad';
    }// getHeader();

    /**
     * Normalizes modal models so read-only views show country and province names
     * when display values are available in list rows.
     *
     * @private
     *
     * @param model - Optional source model.
     *
     * @returns The normalized model used by the form definition.
     */
    private normalizeModel(model: Partial<EntidadModel> | null): ReactiveFormValue<EntidadModel> {
        const source = model ?? {};
        const isReadOnlyModal = this.formType === FormType.INFORMATIVE || this.formType === FormType.DELETION;
        const province = isReadOnlyModal ? (source.provincia_nombre ?? source.provincia) : source.provincia;
        const country = isReadOnlyModal && source.pais ? getBackNameCountry(String(source.pais)) : source.pais;

        return {
            ...source,
            pais: country,
            provincia: province,
        } as ReactiveFormValue<EntidadModel>;
    }// normalizeModel();

    /**
     * Appends visible fields and labels to the reusable table/export metadata.
     *
     * @private
     *
     * @param fields - Fields to inspect.
     *
     * @returns Nothing.
     */
    private appendVisibleFields(fields: FormField<EntidadModel>[]): void {
        const visibleFields = fields.filter((it) => it.conditions.isVisible);

        this.fieldNames = this.fieldNames.concat(visibleFields);
        this.fieldLabels = this.fieldLabels.concat(visibleFields.map((it) => it.label));
    }// appendVisibleFields();
}// class;
